import { z } from "zod";
import { CURRENCY_CODES } from "../money/currency.ts";

/**
 * Inventory contracts (`11-inventory.md`).
 *
 * Quantities cross the wire as decimal strings for the same reason money does:
 * they are scaled integers on both sides, and a JSON number would round 2.001
 * kg somewhere in the middle. `parseStockQuantity` and `formatStockQuantity`
 * are the only places the conversion happens.
 */

const Quantity = z.string().regex(/^-?\d+(\.\d{1,3})?$/, "at most three decimal places");

const PositiveQuantity = z.string().regex(/^\d+(\.\d{1,3})?$/, "at most three decimal places");

const MinorUnits = z.string().regex(/^-?\d+$/, "minor units, as digits");
const PositiveMinorUnits = z.string().regex(/^\d+$/, "minor units, as digits");

export const VALUATION_POLICIES = ["fifo", "lifo"] as const;
export const UNIT_DIMENSIONS = ["mass", "volume", "count"] as const;

// ── Warehouses ──────────────────────────────────────────────────────────────

export const WarehouseSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  name: z.string(),
  contactPerson: z.string().nullable(),
  contactNumber: z.string().nullable(),
  address: z.string().nullable(),
  isDefault: z.boolean(),
  valuationPolicy: z.enum(VALUATION_POLICIES),
  isActive: z.boolean(),
  sort: z.number().int(),
});
export type Warehouse = z.infer<typeof WarehouseSchema>;

export const SaveWarehouseSchema = z.object({
  number: z.string().min(1).max(40),
  name: z.string().min(1).max(120),
  contactPerson: z.string().max(120).nullable().default(null),
  contactNumber: z.string().max(40).nullable().default(null),
  address: z.string().max(240).nullable().default(null),
  isDefault: z.boolean().default(false),
  valuationPolicy: z.enum(VALUATION_POLICIES).default("fifo"),
  isActive: z.boolean().default(true),
  sort: z.number().int().min(0).default(0),
});

// ── Units and conversions ───────────────────────────────────────────────────

export const SupplyUnitSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  name: z.string(),
  dimension: z.enum(UNIT_DIMENSIONS),
  isSystem: z.boolean(),
  sort: z.number().int(),
});
export type SupplyUnit = z.infer<typeof SupplyUnitSchema>;

export const SaveSupplyUnitSchema = z.object({
  number: z.string().min(1).max(20),
  name: z.string().min(1).max(60),
  dimension: z.enum(UNIT_DIMENSIONS),
  sort: z.number().int().min(0).default(0),
});

export const UnitConversionSchema = z.object({
  id: z.uuid(),
  /** Null is a store-wide rule; a supply overrides it. */
  supplyId: z.uuid().nullable(),
  supplyName: z.string().nullable(),
  fromUnitId: z.uuid(),
  fromUnitName: z.string(),
  toUnitId: z.uuid(),
  toUnitName: z.string(),
  /** How many of `toUnit` make one of `fromUnit`. */
  factor: Quantity,
});
export type UnitConversion = z.infer<typeof UnitConversionSchema>;

export const SaveUnitConversionSchema = z.object({
  supplyId: z.uuid().nullable().default(null),
  fromUnitId: z.uuid(),
  toUnitId: z.uuid(),
  factor: PositiveQuantity,
});

// ── Categories and brands ───────────────────────────────────────────────────

export const SupplyCategorySchema = z.object({
  id: z.uuid(),
  parentId: z.uuid().nullable(),
  number: z.string(),
  name: z.string(),
  sort: z.number().int(),
  /** Only meaningful on a top-level row — the source shows `Sub-category Qty`. */
  childCount: z.number().int(),
  supplyCount: z.number().int(),
});
export type SupplyCategory = z.infer<typeof SupplyCategorySchema>;

export const SaveSupplyCategorySchema = z.object({
  parentId: z.uuid().nullable().default(null),
  number: z.string().min(1).max(40),
  name: z.string().min(1).max(120),
  sort: z.number().int().min(0).default(0),
});

export const SupplyBrandSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  sort: z.number().int(),
});
export type SupplyBrand = z.infer<typeof SupplyBrandSchema>;

export const SaveSupplyBrandSchema = z.object({
  number: z.string().min(1).max(40),
  name: z.string().min(1).max(120),
  description: z.string().max(240).nullable().default(null),
  sort: z.number().int().min(0).default(0),
});

// ── Suppliers ───────────────────────────────────────────────────────────────

export const SupplierSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  name: z.string(),
  nickname: z.string().nullable(),
  profile: z.string().nullable(),
  address: z.string().nullable(),
  contactPerson: z.string().nullable(),
  contactNumber: z.string().nullable(),
  isActive: z.boolean(),
  supplyCount: z.number().int(),
  sort: z.number().int(),
});
export type Supplier = z.infer<typeof SupplierSchema>;

export const SaveSupplierSchema = z.object({
  number: z.string().min(1).max(40),
  name: z.string().min(1).max(120),
  nickname: z.string().max(120).nullable().default(null),
  profile: z.string().max(500).nullable().default(null),
  address: z.string().max(240).nullable().default(null),
  contactPerson: z.string().max(120).nullable().default(null),
  contactNumber: z.string().max(40).nullable().default(null),
  isActive: z.boolean().default(true),
  sort: z.number().int().min(0).default(0),
});

export const SupplierPriceSchema = z.object({
  id: z.uuid(),
  supplyId: z.uuid(),
  supplyName: z.string(),
  purchasePrice: MinorUnits,
  purchaseCurrency: z.enum(CURRENCY_CODES),
  purchaseUnitId: z.uuid(),
  purchaseUnitName: z.string(),
  isPreferred: z.boolean(),
});
export type SupplierPrice = z.infer<typeof SupplierPriceSchema>;

export const SaveSupplierPricesSchema = z.object({
  supplies: z.array(
    z.object({
      supplyId: z.uuid(),
      purchasePrice: PositiveMinorUnits,
      purchaseUnitId: z.uuid(),
      isPreferred: z.boolean().default(false),
    }),
  ),
});

// ── Supplies ───────────────────────────────────────────────────────────────

export const SupplySchema = z.object({
  id: z.uuid(),
  number: z.string(),
  name: z.string(),
  otherName: z.string().nullable(),
  categoryId: z.uuid().nullable(),
  categoryName: z.string().nullable(),
  brandId: z.uuid().nullable(),
  brandName: z.string().nullable(),
  barcode: z.string().nullable(),
  stockUnitId: z.uuid(),
  stockUnitName: z.string(),
  salesUnitId: z.uuid().nullable(),
  purchaseUnitId: z.uuid().nullable(),
  minimumStockQuantity: Quantity,
  defaultSupplierId: z.uuid().nullable(),
  picUrl: z.string().nullable(),
  isActive: z.boolean(),
  sort: z.number().int(),
  /** Across every warehouse, in the stock unit. */
  stockQuantity: Quantity,
  isBelowMinimum: z.boolean(),
});
export type Supply = z.infer<typeof SupplySchema>;

export const SaveSupplySchema = z.object({
  number: z.string().min(1).max(40),
  name: z.string().min(1).max(120),
  otherName: z.string().max(120).nullable().default(null),
  categoryId: z.uuid().nullable().default(null),
  brandId: z.uuid().nullable().default(null),
  /** Scanned as well as typed *(verified)*, and unique within the store. */
  barcode: z.string().max(60).nullable().default(null),
  stockUnitId: z.uuid(),
  salesUnitId: z.uuid().nullable().default(null),
  purchaseUnitId: z.uuid().nullable().default(null),
  minimumStockQuantity: PositiveQuantity.default("0"),
  defaultSupplierId: z.uuid().nullable().default(null),
  picUrl: z.string().max(500).nullable().default(null),
  isActive: z.boolean().default(true),
  sort: z.number().int().min(0).default(0),
});

// ── Documents (§ 5) ─────────────────────────────────────────────────────────

export const PURCHASE_TYPES = ["supplier", "market", "head_office"] as const;
export const PURCHASE_STATUSES = [
  "purchasing",
  "head_office_reviewing",
  "head_office_rejected",
  "pending_inbound",
  "ended",
  "voided",
] as const;
export const INBOUND_TYPES = [
  "supplier",
  "market",
  "item_return",
  "overstock",
  "transfer",
] as const;
export const INBOUND_STATUSES = [
  "draft",
  "waiting_review",
  "approving",
  "received",
  "rejected",
] as const;
export const OUTBOUND_TYPES = ["loss", "sales", "transfer", "item_sales", "shortage"] as const;
export const OUTBOUND_STATUSES = [
  "draft",
  "waiting_review",
  "issued",
  "transferring",
  "rejected",
  "blocked",
] as const;
export const COUNT_SCOPES = ["picked", "all"] as const;
export const COUNT_STATUSES = ["counting", "waiting_review", "adjusted", "voided"] as const;

const DocumentLine = z.object({
  id: z.uuid(),
  supplyId: z.uuid(),
  supplyName: z.string(),
  unitId: z.uuid(),
  unitName: z.string(),
  quantity: PositiveQuantity,
});

export const PurchaseOrderSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  warehouseId: z.uuid(),
  warehouseName: z.string(),
  supplierId: z.uuid().nullable(),
  supplierName: z.string().nullable(),
  purchaseType: z.enum(PURCHASE_TYPES),
  status: z.enum(PURCHASE_STATUSES),
  purchaserName: z.string().nullable(),
  remarks: z.string().nullable(),
  headOfficeRemarks: z.string().nullable(),
  voidReason: z.string().nullable(),
  totalAmount: MinorUnits,
  currency: z.enum(CURRENCY_CODES),
  createdAt: z.string(),
  lines: z.array(
    DocumentLine.extend({
      freeQuantity: PositiveQuantity,
      unitPrice: MinorUnits,
    }),
  ),
});
export type PurchaseOrder = z.infer<typeof PurchaseOrderSchema>;

export const SavePurchaseOrderSchema = z.object({
  warehouseId: z.uuid(),
  supplierId: z.uuid().nullable().default(null),
  purchaseType: z.enum(PURCHASE_TYPES),
  remarks: z.string().max(500).nullable().default(null),
  lines: z
    .array(
      z.object({
        supplyId: z.uuid(),
        quantity: PositiveQuantity,
        freeQuantity: PositiveQuantity.default("0"),
        unitId: z.uuid(),
        unitPrice: PositiveMinorUnits,
      }),
    )
    .min(1),
});

export const VoidPurchaseSchema = z.object({ reason: z.string().min(1).max(240) });

export const InboundTicketSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  warehouseId: z.uuid(),
  warehouseName: z.string(),
  inboundType: z.enum(INBOUND_TYPES),
  sourceDocumentId: z.uuid().nullable(),
  status: z.enum(INBOUND_STATUSES),
  remarks: z.string().nullable(),
  rejectReason: z.string().nullable(),
  proofUrls: z.array(z.string()),
  inboundTime: z.string().nullable(),
  currency: z.enum(CURRENCY_CODES),
  createdAt: z.string(),
  lines: z.array(
    DocumentLine.extend({
      batchNo: z.string().nullable(),
      freeQuantity: PositiveQuantity,
      unitCost: MinorUnits,
    }),
  ),
});
export type InboundTicket = z.infer<typeof InboundTicketSchema>;

export const SaveInboundTicketSchema = z.object({
  warehouseId: z.uuid(),
  inboundType: z.enum(INBOUND_TYPES),
  sourceDocumentId: z.uuid().nullable().default(null),
  remarks: z.string().max(500).nullable().default(null),
  proofUrls: z.array(z.string().max(500)).default([]),
  lines: z
    .array(
      z.object({
        supplyId: z.uuid(),
        batchNo: z.string().max(60).nullable().default(null),
        quantity: PositiveQuantity,
        freeQuantity: PositiveQuantity.default("0"),
        unitId: z.uuid(),
        /** What was paid per purchase unit, before free quantity dilutes it. */
        unitCost: PositiveMinorUnits,
        expiresAt: z.string().nullable().default(null),
      }),
    )
    .min(1),
});

export const RejectSchema = z.object({ reason: z.string().min(1).max(240) });

export const OutboundOrderSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  warehouseId: z.uuid(),
  warehouseName: z.string(),
  outboundType: z.enum(OUTBOUND_TYPES),
  status: z.enum(OUTBOUND_STATUSES),
  blockedReason: z.string().nullable(),
  remarks: z.string().nullable(),
  currency: z.enum(CURRENCY_CODES),
  salesAmount: MinorUnits.nullable(),
  customerName: z.string().nullable(),
  customerContactPerson: z.string().nullable(),
  customerPhone: z.string().nullable(),
  destinationWarehouseId: z.uuid().nullable(),
  outboundTime: z.string().nullable(),
  createdAt: z.string(),
  lines: z.array(
    DocumentLine.extend({
      /** What the batch draws cost. Zero until the document is issued. */
      cost: MinorUnits,
      shortfall: Quantity,
    }),
  ),
});
export type OutboundOrder = z.infer<typeof OutboundOrderSchema>;

export const SaveOutboundOrderSchema = z.object({
  warehouseId: z.uuid(),
  outboundType: z.enum(["loss", "sales", "transfer"]),
  remarks: z.string().max(500).nullable().default(null),
  salesAmount: PositiveMinorUnits.nullable().default(null),
  customerName: z.string().max(120).nullable().default(null),
  customerContactPerson: z.string().max(120).nullable().default(null),
  customerPhone: z.string().max(40).nullable().default(null),
  destinationWarehouseId: z.uuid().nullable().default(null),
  lines: z
    .array(z.object({ supplyId: z.uuid(), quantity: PositiveQuantity, unitId: z.uuid() }))
    .min(1),
});

export const StockCountSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  warehouseId: z.uuid(),
  warehouseName: z.string(),
  scope: z.enum(COUNT_SCOPES),
  status: z.enum(COUNT_STATUSES),
  counterName: z.string().nullable(),
  creatorName: z.string().nullable(),
  creatorPhone: z.string().nullable(),
  adjustedAt: z.string().nullable(),
  remarks: z.string().nullable(),
  createdAt: z.string(),
  lines: z.array(
    DocumentLine.omit({ quantity: true }).extend({
      expectedQuantity: Quantity,
      countedQuantity: Quantity.nullable(),
      /** `counted − expected`. Null until it has been counted. */
      difference: Quantity.nullable(),
    }),
  ),
});
export type StockCount = z.infer<typeof StockCountSchema>;

export const StartStockCountSchema = z.object({
  warehouseId: z.uuid(),
  scope: z.enum(COUNT_SCOPES).default("all"),
  /** Required for a `picked` count; ignored for `all`. */
  supplyIds: z.array(z.uuid()).default([]),
  counterName: z.string().max(120).nullable().default(null),
  creatorPhone: z.string().max(40).nullable().default(null),
  remarks: z.string().max(500).nullable().default(null),
});

export const SaveStockCountLinesSchema = z.object({
  lines: z.array(z.object({ id: z.uuid(), countedQuantity: PositiveQuantity })).min(1),
});

/** What the stock report answers, per supply per warehouse (§ 8). */
export const StockPeriodRowSchema = z.object({
  warehouseId: z.uuid(),
  warehouseName: z.string(),
  supplyId: z.uuid(),
  supplyName: z.string(),
  unitName: z.string(),
  initial: Quantity,
  inbound: Quantity,
  outbound: Quantity,
  ending: Quantity,
});
export type StockPeriodRow = z.infer<typeof StockPeriodRowSchema>;

/** A live balance, with the batches behind it (`11-inventory.md` § 9). */
export const StockBalanceSchema = z.object({
  warehouseId: z.uuid(),
  warehouseName: z.string(),
  supplyId: z.uuid(),
  supplyNumber: z.string(),
  supplyName: z.string(),
  unitName: z.string(),
  quantity: Quantity,
  isBelowMinimum: z.boolean(),
  minimumStockQuantity: Quantity,
  /** `Σ quantity × unit_cost` across live batches. */
  value: MinorUnits,
  currency: z.enum(CURRENCY_CODES),
});
export type StockBalance = z.infer<typeof StockBalanceSchema>;

export const StockBatchSchema = z.object({
  id: z.uuid(),
  batchNo: z.string(),
  warehouseName: z.string(),
  quantityReceived: Quantity,
  quantityRemaining: Quantity,
  unitCost: MinorUnits,
  currency: z.enum(CURRENCY_CODES),
  receivedAt: z.string(),
  expiresAt: z.string().nullable(),
});
export type StockBatch = z.infer<typeof StockBatchSchema>;

// ── Menu linkage (§ 6) ──────────────────────────────────────────────────────

/**
 * A recipe line. Three linkage points, all evidenced: the item itself, a
 * specification option (a Large coffee draws more beans than a Small), and a
 * topping item (`material_relation_dto` is verified on topping items).
 */
export const RecipeLineSchema = z.object({
  id: z.uuid(),
  supplyId: z.uuid(),
  supplyName: z.string(),
  quantity: PositiveQuantity,
  unitId: z.uuid(),
  unitName: z.string(),
  specificationOptionId: z.uuid().nullable(),
  specificationOptionName: z.string().nullable(),
  toppingItemId: z.uuid().nullable(),
  toppingItemName: z.string().nullable(),
});
export type RecipeLine = z.infer<typeof RecipeLineSchema>;

export const SaveRecipeSchema = z.object({
  lines: z.array(
    z.object({
      supplyId: z.uuid(),
      quantity: PositiveQuantity,
      unitId: z.uuid(),
      specificationOptionId: z.uuid().nullable().default(null),
      toppingItemId: z.uuid().nullable().default(null),
    }),
  ),
});

/** What a supply's clearance decision needs: the dishes that consume it. */
export const SupplyConsumerSchema = z.object({
  itemId: z.uuid(),
  itemName: z.string(),
  price: MinorUnits,
  currency: z.enum(CURRENCY_CODES),
  quantity: PositiveQuantity,
  unitName: z.string(),
});
export type SupplyConsumer = z.infer<typeof SupplyConsumerSchema>;

// ── Transfers and chain requisitions (§§ 5.4, 7) ────────────────────────────

/**
 * Receiving a transfer. The destination warehouse can reject it *(verified —
 * "Target Warehouse Rejected")*, which is what makes a transfer a two-party
 * agreement: stock in flight belongs to neither side until somebody accepts.
 */
export const ReceiveTransferSchema = z.object({
  accept: z.boolean(),
  reason: z.string().max(240).nullable().default(null),
});

/** Head office's answer to a substore's requisition (`11-inventory.md` § 7). */
export const ReviewRequisitionSchema = z.object({
  approve: z.boolean(),
  remarks: z.string().max(500).nullable().default(null),
});

export const RequisitionSchema = PurchaseOrderSchema.extend({
  storeId: z.uuid(),
  storeName: z.string(),
});
export type Requisition = z.infer<typeof RequisitionSchema>;
